/**
 * The ONE admin-pickable icon vocabulary (#14985).
 *
 * Dashboards, scorecards, announcements, meeting agenda sections and
 * terminology all store an icon as a NAME and resolve it here. Before #14985
 * each surface had its own hand-maintained list — four of them, two naming
 * conventions — so what an admin could pick depended on which page they were
 * on, and a name valid on one surface rendered a fallback on another.
 *
 * The list is the exact union of what those four offered or rendered. Names
 * are kebab-case Lucide names. Pickers (`@/components/icon-picker`) only ever
 * WRITE a canonical name; older stored values (meeting-section PascalCase,
 * dashboard emoji, `bar-chart`) keep resolving through
 * {@link LEGACY_ICON_ALIASES} and are rewritten to canonical form the next
 * time their record is saved. No backfill.
 *
 * ## No `'use client'`
 *
 * `sectionIcon` delegates here and is called from an `async` SERVER component
 * (`content-review.tsx`). A `'use client'` directive would turn every export
 * into a client reference and the server call would throw — see the note in
 * `src/components/meetings/sections/section-icons.ts`.
 *
 * ## Fallbacks stay per-surface
 *
 * {@link resolveIcon} answers `null` for an unknown value. Each surface keeps
 * its own fallback glyph (`resolveIcon(v) ?? FALLBACK`), so rendering for an
 * unknown value is what it was before.
 */

import {
  Activity,
  AlertTriangle,
  Archive,
  Award,
  BarChart3,
  BookOpen,
  Bookmark,
  Box,
  Briefcase,
  Building2,
  Calendar,
  CalendarDays,
  CheckSquare,
  Circle,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Coffee,
  Compass,
  Component,
  Dices,
  DollarSign,
  FileText,
  Flag,
  Folder,
  Gauge,
  Gem,
  Handshake,
  Heart,
  HeartPulse,
  Info,
  Landmark,
  Layers,
  LayoutDashboard,
  Lightbulb,
  ListChecks,
  type LucideIcon,
  Map as MapIcon,
  Megaphone,
  MessageSquare,
  Mountain,
  NotebookPen,
  Package,
  Radar,
  Rocket,
  Settings,
  Sparkles,
  Square,
  SquareCheck,
  Star,
  Stone,
  Tag,
  Target,
  TrendingUp,
  Trophy,
  User,
  Users,
  UsersRound,
  Wrench,
  Zap,
} from '@/components/icons'

/** One pickable icon: the stored NAME, its glyph, how it reads, its group. */
export interface IconEntry {
  name: string
  icon: LucideIcon
  label: string
  group: string
}

/**
 * Every pickable icon, in picker order. Groups are contiguous; the picker
 * renders a heading wherever the group changes.
 */
export const ICON_ENTRIES: readonly IconEntry[] = [
  // Goals & Strategy
  { name: 'target', icon: Target, label: 'Target', group: 'Goals & Strategy' },
  { name: 'flag', icon: Flag, label: 'Flag', group: 'Goals & Strategy' },
  { name: 'trophy', icon: Trophy, label: 'Trophy', group: 'Goals & Strategy' },
  { name: 'award', icon: Award, label: 'Award', group: 'Goals & Strategy' },
  { name: 'star', icon: Star, label: 'Star', group: 'Goals & Strategy' },
  { name: 'gem', icon: Gem, label: 'Gem', group: 'Goals & Strategy' },
  { name: 'stone', icon: Stone, label: 'Stone', group: 'Goals & Strategy' },
  {
    name: 'compass',
    icon: Compass,
    label: 'Direction',
    group: 'Goals & Strategy',
  },
  // Work Items
  { name: 'mountain', icon: Mountain, label: 'Mountain', group: 'Work Items' },
  { name: 'rocket', icon: Rocket, label: 'Rocket', group: 'Work Items' },
  { name: 'dices', icon: Dices, label: 'Dice', group: 'Work Items' },
  {
    name: 'lightbulb',
    icon: Lightbulb,
    label: 'Lightbulb',
    group: 'Work Items',
  },
  { name: 'zap', icon: Zap, label: 'Zap', group: 'Work Items' },
  { name: 'sparkles', icon: Sparkles, label: 'Sparkles', group: 'Work Items' },
  { name: 'wrench', icon: Wrench, label: 'Operations', group: 'Work Items' },
  // Teams & People
  { name: 'users', icon: Users, label: 'Users', group: 'Teams & People' },
  {
    name: 'users-round',
    icon: UsersRound,
    label: 'Users (Round)',
    group: 'Teams & People',
  },
  { name: 'user', icon: User, label: 'User', group: 'Teams & People' },
  {
    name: 'building-2',
    icon: Building2,
    label: 'Building',
    group: 'Teams & People',
  },
  {
    name: 'landmark',
    icon: Landmark,
    label: 'Landmark',
    group: 'Teams & People',
  },
  {
    name: 'component',
    icon: Component,
    label: 'Component',
    group: 'Teams & People',
  },
  {
    name: 'briefcase',
    icon: Briefcase,
    label: 'Briefcase',
    group: 'Teams & People',
  },
  {
    name: 'handshake',
    icon: Handshake,
    label: 'Partners',
    group: 'Teams & People',
  },
  // Meetings & Events
  {
    name: 'calendar',
    icon: Calendar,
    label: 'Calendar',
    group: 'Meetings & Events',
  },
  {
    name: 'calendar-days',
    icon: CalendarDays,
    label: 'Calendar Days',
    group: 'Meetings & Events',
  },
  { name: 'clock', icon: Clock, label: 'Time', group: 'Meetings & Events' },
  { name: 'coffee', icon: Coffee, label: 'Coffee', group: 'Meetings & Events' },
  // Check-ins & Reviews
  {
    name: 'clipboard-check',
    icon: ClipboardCheck,
    label: 'Clipboard Check',
    group: 'Check-ins & Reviews',
  },
  {
    name: 'clipboard-list',
    icon: ClipboardList,
    label: 'Clipboard List',
    group: 'Check-ins & Reviews',
  },
  {
    name: 'list-checks',
    icon: ListChecks,
    label: 'List Checks',
    group: 'Check-ins & Reviews',
  },
  // Organization
  { name: 'folder', icon: Folder, label: 'Folder', group: 'Organization' },
  { name: 'package', icon: Package, label: 'Package', group: 'Organization' },
  { name: 'box', icon: Box, label: 'Box', group: 'Organization' },
  { name: 'archive', icon: Archive, label: 'Archive', group: 'Organization' },
  { name: 'layers', icon: Layers, label: 'Layers', group: 'Organization' },
  { name: 'map', icon: MapIcon, label: 'Map', group: 'Organization' },
  {
    name: 'layout-dashboard',
    icon: LayoutDashboard,
    label: 'Dashboard',
    group: 'Organization',
  },
  {
    name: 'settings',
    icon: Settings,
    label: 'Settings',
    group: 'Organization',
  },
  // Action & Progress
  {
    name: 'trending-up',
    icon: TrendingUp,
    label: 'Trending Up',
    group: 'Action & Progress',
  },
  {
    name: 'bar-chart-3',
    icon: BarChart3,
    label: 'Chart',
    group: 'Action & Progress',
  },
  { name: 'gauge', icon: Gauge, label: 'Gauge', group: 'Action & Progress' },
  {
    name: 'activity',
    icon: Activity,
    label: 'Activity',
    group: 'Action & Progress',
  },
  {
    name: 'heart-pulse',
    icon: HeartPulse,
    label: 'Heart Pulse',
    group: 'Action & Progress',
  },
  { name: 'radar', icon: Radar, label: 'Radar', group: 'Action & Progress' },
  {
    name: 'dollar-sign',
    icon: DollarSign,
    label: 'Finance',
    group: 'Action & Progress',
  },
  // Notes & Logs
  {
    name: 'notebook-pen',
    icon: NotebookPen,
    label: 'Notebook Pen',
    group: 'Notes & Logs',
  },
  { name: 'tag', icon: Tag, label: 'Tag', group: 'Notes & Logs' },
  {
    name: 'file-text',
    icon: FileText,
    label: 'File Text',
    group: 'Notes & Logs',
  },
  {
    name: 'book-open',
    icon: BookOpen,
    label: 'Book Open',
    group: 'Notes & Logs',
  },
  // Communication
  {
    name: 'megaphone',
    icon: Megaphone,
    label: 'Megaphone',
    group: 'Communication',
  },
  { name: 'info', icon: Info, label: 'Info', group: 'Communication' },
  {
    name: 'alert-triangle',
    icon: AlertTriangle,
    label: 'Alert Triangle',
    group: 'Communication',
  },
  {
    name: 'message-square',
    icon: MessageSquare,
    label: 'Message Square',
    group: 'Communication',
  },
  // Other
  { name: 'bookmark', icon: Bookmark, label: 'Bookmark', group: 'Other' },
  { name: 'heart', icon: Heart, label: 'Heart', group: 'Other' },
  { name: 'circle', icon: Circle, label: 'Circle', group: 'Other' },
  { name: 'square', icon: Square, label: 'Square', group: 'Other' },
  {
    name: 'square-check',
    icon: SquareCheck,
    label: 'Square Check',
    group: 'Other',
  },
  {
    name: 'check-square',
    icon: CheckSquare,
    label: 'Check Square',
    group: 'Other',
  },
]

/** Every canonical name, in picker order. */
export const ICON_NAMES: readonly string[] = ICON_ENTRIES.map(e => e.name)

/**
 * Stored values that are NOT canonical names but must keep rendering: they
 * resolve, they are never offered. Each maps to a canonical name.
 */
export const LEGACY_ICON_ALIASES: Readonly<Record<string, string>> = {
  // The dashboard map's duplicate key.
  'bar-chart': 'bar-chart-3',
  // Dashboard emoji, from before the Lucide switch.
  '📊': 'bar-chart-3',
  '💰': 'dollar-sign',
  '🎯': 'target',
  '🚀': 'rocket',
  '📈': 'trending-up',
  '🏆': 'trophy',
  '⚡': 'zap',
  '🔧': 'wrench',
  '💡': 'lightbulb',
  '🤝': 'handshake',
  // Meeting-section PascalCase names (#13931 – #14054).
  AlertTriangle: 'alert-triangle',
  BarChart3: 'bar-chart-3',
  Briefcase: 'briefcase',
  Calendar: 'calendar',
  CheckSquare: 'check-square',
  Coffee: 'coffee',
  Compass: 'compass',
  FileText: 'file-text',
  Flag: 'flag',
  Gem: 'gem',
  Heart: 'heart',
  Info: 'info',
  ListChecks: 'list-checks',
  Megaphone: 'megaphone',
  MessageSquare: 'message-square',
  Sparkles: 'sparkles',
  TrendingUp: 'trending-up',
  Users: 'users',
}

const BY_NAME: Readonly<Record<string, IconEntry>> = Object.fromEntries(
  ICON_ENTRIES.map(entry => [entry.name, entry]),
)

function hasOwn(record: object, key: string): boolean {
  // Own-property only. A bare index reaches the prototype chain, where
  // `__proto__` / `constructor` / `toString` are all non-nullish, and would
  // hand a renderer an object or a native function where a component belongs
  // — which throws in render and takes a live meeting route down (#14055).
  return Object.prototype.hasOwnProperty.call(record, key)
}

/**
 * Any accepted input — a canonical name, a legacy alias, a dashboard emoji, a
 * meeting-section PascalCase name — as its canonical kebab name, or `null`.
 */
export function normalizeIconName(
  value: string | null | undefined,
): string | null {
  if (!value) return null
  if (hasOwn(BY_NAME, value)) return value
  if (hasOwn(LEGACY_ICON_ALIASES, value)) {
    return LEGACY_ICON_ALIASES[value] ?? null
  }
  return null
}

/** The glyph for any accepted input, or `null` for nothing / unknown. */
export function resolveIcon(
  value: string | null | undefined,
): LucideIcon | null {
  const name = normalizeIconName(value)
  return name === null ? null : (BY_NAME[name]?.icon ?? null)
}

export type IconInputResult =
  | { ok: true; value: string | null | undefined }
  | { ok: false; error: string }

/**
 * Server-side check for an icon name submitted by a form: `undefined` means
 * "not being changed", empty means "no icon", anything else must normalize
 * to a canonical name — which is what gets stored.
 */
export function parseIconInput(
  value: string | null | undefined,
): IconInputResult {
  if (value === undefined) return { ok: true, value: undefined }
  if (value === null || value === '') return { ok: true, value: null }
  const name = normalizeIconName(value)
  if (name === null) return { ok: false, error: 'Choose an icon from the list' }
  return { ok: true, value: name }
}
